import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Space, Typography } from 'antd';
import { ReloadOutlined, SoundOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { EChartsOption } from 'echarts';
import ChartCard from '@/components/ChartCard';
import PageContainer from '@/components/PageContainer';
import { useRTTempPrice } from '@/api/rtTempPrice';
import { BASE_DATAZOOM, format2 } from '@/utils/charts';
import type { DeclaredStoragePlanRow, RTTempPriceRow } from '@/types/api';

const { Text } = Typography;

const PRICE_JUMP_THRESHOLD = 200;
const DEFAULT_PRICE_AREA_HALF_INTERVAL_MS = 7.5 * 60 * 1000;
const NEGATIVE_PRICE_ALERT_LEAD_MINUTES = 60;
const VOICE_ENABLED_STORAGE_KEY = 'dispatch-sop-voice-enabled';
const DECLARED_PLAN_INTERVAL_HOURS = 0.25;
const MORNING_MANUAL_WINDOW = { start: 6 * 60 + 30, end: 9 * 60 };
const EVENING_MANUAL_WINDOW = { start: 16 * 60, end: 18 * 60 };

type PriceRiskWindow = {
  startTime: number;
  endTime: number;
  startLabel: string;
  endLabel: string;
  hasNegativePrice: boolean;
};

type NegativePriceAlert = {
  targetTime: string;
  label: string;
  price: number;
};

type ManualJudgementHint = {
  message: string;
  description: string;
};

type DeclaredPlanSummary = {
  chargeEnergyMwh: number;
  dischargeEnergyMwh: number;
  nextChargeLabel: string | null;
  nextChargePowerMw: number | null;
};

function displayRefreshTime(value?: number) {
  return value ? dayjs(value).format('HH:mm:ss') : '-';
}

function getMarketDayBounds(now = dayjs()) {
  const start = now.startOf('day').add(15, 'minute').valueOf();
  const end = now.add(1, 'day').startOf('day').valueOf();
  return { start, end };
}

function toTimeValue(value: string) {
  return dayjs(value).valueOf();
}

function buildPriceSeries(prices: RTTempPriceRow[]) {
  return prices.map((price) => [toTimeValue(price.target_time), price.price_rt_temp_final]);
}

function buildDeclaredPlanSeries(plans: DeclaredStoragePlanRow[]) {
  return plans.map((plan) => [toTimeValue(plan.target_time), plan.declared_power_mw]);
}

function isWithinMinuteWindow(now: dayjs.Dayjs, startMinute: number, endMinute: number) {
  const currentMinute = now.hour() * 60 + now.minute();
  return currentMinute >= startMinute && currentMinute <= endMinute;
}

function buildManualJudgementHint(now = dayjs()): ManualJudgementHint | null {
  if (isWithinMinuteWindow(now, MORNING_MANUAL_WINDOW.start, MORNING_MANUAL_WINDOW.end)) {
    return {
      message: '人工判断提示：早间 SOC/天气校准窗口',
      description: '请结合 SOC、未来 2 小时天气和现场实际出力，判断是否需要小幅下压超短期功率预测。',
    };
  }

  if (isWithinMinuteWindow(now, EVENING_MANUAL_WINDOW.start, EVENING_MANUAL_WINDOW.end)) {
    return {
      message: '人工判断提示：傍晚 SOC 保护窗口',
      description: '请确认是否需要保持预测准确，避免晚高峰放电计划前提前释放 SOC。',
    };
  }

  return null;
}

function buildDeclaredPlanSummary(
  plans: DeclaredStoragePlanRow[],
  marketDayBounds: { start: number; end: number },
  now = dayjs(),
): DeclaredPlanSummary {
  return plans.reduce<DeclaredPlanSummary>((summary, plan) => {
    const targetTime = toTimeValue(plan.target_time);
    if (targetTime < marketDayBounds.start || targetTime > marketDayBounds.end) {
      return summary;
    }

    const power = Number(plan.declared_power_mw);
    if (!Number.isFinite(power)) {
      return summary;
    }

    if (power < 0) {
      summary.chargeEnergyMwh += Math.abs(power) * DECLARED_PLAN_INTERVAL_HOURS;
      if (targetTime >= now.valueOf() && summary.nextChargeLabel === null) {
        summary.nextChargeLabel = dayjs(targetTime).format('HH:mm');
        summary.nextChargePowerMw = power;
      }
    } else if (power > 0) {
      summary.dischargeEnergyMwh += power * DECLARED_PLAN_INTERVAL_HOURS;
    }

    return summary;
  }, {
    chargeEnergyMwh: 0,
    dischargeEnergyMwh: 0,
    nextChargeLabel: null,
    nextChargePowerMw: null,
  });
}

function getPriceTime(prices: RTTempPriceRow[], index: number) {
  return toTimeValue(prices[index].target_time);
}

function getHalfInterval(startTime: number, endTime: number) {
  const interval = endTime - startTime;
  return interval > 0 ? interval / 2 : DEFAULT_PRICE_AREA_HALF_INTERVAL_MS;
}

function getPriceAreaWindow(prices: RTTempPriceRow[], index: number) {
  const currentTime = getPriceTime(prices, index);
  const previousTime = index > 0 ? getPriceTime(prices, index - 1) : null;
  const nextTime = index < prices.length - 1 ? getPriceTime(prices, index + 1) : null;
  const beforeHalfInterval = previousTime !== null
    ? getHalfInterval(previousTime, currentTime)
    : nextTime !== null
      ? getHalfInterval(currentTime, nextTime)
      : DEFAULT_PRICE_AREA_HALF_INTERVAL_MS;
  const afterHalfInterval = nextTime !== null
    ? getHalfInterval(currentTime, nextTime)
    : previousTime !== null
      ? getHalfInterval(previousTime, currentTime)
      : DEFAULT_PRICE_AREA_HALF_INTERVAL_MS;

  return [currentTime - beforeHalfInterval, currentTime + afterHalfInterval];
}

function getInitialFinalDelta(price: RTTempPriceRow) {
  if (typeof price.price_rt_temp_delta === 'number') {
    return price.price_rt_temp_delta;
  }
  if (typeof price.price_rt_temp_initial === 'number') {
    return price.price_rt_temp_final - price.price_rt_temp_initial;
  }
  return null;
}

function hasAdjacentFinalJump(prices: RTTempPriceRow[], index: number) {
  if (index === 0) return false;
  return Math.abs(prices[index].price_rt_temp_final - prices[index - 1].price_rt_temp_final) > PRICE_JUMP_THRESHOLD;
}

function isRiskPrice(prices: RTTempPriceRow[], index: number) {
  const price = prices[index];
  const delta = getInitialFinalDelta(price);
  return (
    price.price_rt_temp_final < 0
    || (typeof delta === 'number' && Math.abs(delta) > PRICE_JUMP_THRESHOLD)
    || hasAdjacentFinalJump(prices, index)
  );
}

function mergeRiskWindows(windows: PriceRiskWindow[]) {
  return windows.reduce<PriceRiskWindow[]>((merged, window) => {
    const previous = merged[merged.length - 1];
    if (!previous || window.startTime > previous.endTime) {
      merged.push({ ...window });
      return merged;
    }

    previous.endTime = Math.max(previous.endTime, window.endTime);
    previous.endLabel = window.endLabel;
    previous.hasNegativePrice = previous.hasNegativePrice || window.hasNegativePrice;
    return merged;
  }, []);
}

function riskWindowName(window: PriceRiskWindow) {
  return window.startLabel === window.endLabel
    ? `风险 ${window.startLabel}`
    : `风险 ${window.startLabel}-${window.endLabel}`;
}

function riskWindowStyle(window: PriceRiskWindow) {
  if (window.hasNegativePrice) {
    return {
      color: 'rgba(220, 38, 38, 0.22)',
      borderColor: '#b91c1c',
      borderWidth: 2,
    };
  }

  return {
    color: 'rgba(245, 158, 11, 0.28)',
    borderColor: '#b45309',
    borderWidth: 2,
  };
}

function buildPriceRiskAreas(prices: RTTempPriceRow[], now = dayjs()) {
  const windows = prices.reduce<PriceRiskWindow[]>((result, price, index) => {
    const targetTime = dayjs(price.target_time);
    if (targetTime.isBefore(now) || !isRiskPrice(prices, index)) {
      return result;
    }

    const label = targetTime.format('HH:mm');
    const [startTime, endTime] = getPriceAreaWindow(prices, index);
    result.push({
      startTime,
      endTime,
      startLabel: label,
      endLabel: label,
      hasNegativePrice: price.price_rt_temp_final < 0,
    });
    return result;
  }, []);

  return mergeRiskWindows(windows)
    .map<[Record<string, unknown>, Record<string, unknown>]>((window) => [
      {
        name: riskWindowName(window),
        xAxis: window.startTime,
        itemStyle: riskWindowStyle(window),
      },
      { xAxis: window.endTime },
    ]);
}

function buildNegativePriceAlerts(
  prices: RTTempPriceRow[],
  now = dayjs(),
  leadMinutes = NEGATIVE_PRICE_ALERT_LEAD_MINUTES,
) {
  return prices.reduce<NegativePriceAlert[]>((alerts, price) => {
    const targetTime = dayjs(price.target_time);
    const lead = targetTime.diff(now, 'minute', true);
    if (lead >= leadMinutes && price.price_rt_temp_final < 0) {
      alerts.push({
        targetTime: price.target_time,
        label: targetTime.format('HH:mm'),
        price: price.price_rt_temp_final,
      });
    }
    return alerts;
  }, []);
}

function formatAlertPrice(value: number) {
  return `${value.toFixed(2)} 元/MWh`;
}

function formatEnergy(value: number) {
  return `${value.toFixed(2)} MWh`;
}

function formatPower(value: number) {
  return `${Math.abs(value).toFixed(2)} MW`;
}

function buildNegativeAlertKey(alerts: NegativePriceAlert[]) {
  return alerts.map((alert) => `${alert.targetTime}:${alert.price.toFixed(2)}`).join('|');
}

function buildNegativeAlertVoiceText(alerts: NegativePriceAlert[]) {
  const first = alerts[0];
  const moreText = alerts.length > 1 ? `等 ${alerts.length} 个时段` : '';
  return [
    '实时调度提醒',
    `未来时段出现负电价，${first.label} ${formatAlertPrice(first.price)}${moreText}`,
    '请运维结合 SOC 和可充状态，考虑下调超短期功率预测',
  ].join('，');
}

function speakText(text: string) {
  if (!('speechSynthesis' in window) || typeof SpeechSynthesisUtterance === 'undefined') {
    return false;
  }

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = 'zh-CN';
  utterance.rate = 1;
  utterance.pitch = 1;
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(utterance);
  return true;
}

export default function DispatchSopPage() {
  const {
    data: rtPriceData,
    isLoading: priceLoading,
    error: priceError,
    refetch: refetchPrice,
    dataUpdatedAt: priceUpdatedAt,
  } = useRTTempPrice();
  const [voiceEnabled, setVoiceEnabled] = useState(() => (
    typeof window !== 'undefined' && window.localStorage.getItem(VOICE_ENABLED_STORAGE_KEY) === 'true'
  ));
  const lastSpokenAlertKeyRef = useRef('');

  const prices = rtPriceData?.data?.prices ?? [];
  const declaredPlans = rtPriceData?.data?.declared_plans ?? [];
  const now = dayjs();
  const marketDayBounds = getMarketDayBounds(now);
  const priceRiskAreas = buildPriceRiskAreas(prices, now);
  const negativePriceAlerts = useMemo(
    () => buildNegativePriceAlerts(prices, now),
    [prices, now],
  );
  const negativePriceAlertKey = useMemo(() => buildNegativeAlertKey(negativePriceAlerts), [negativePriceAlerts]);
  const negativePriceVoiceText = useMemo(
    () => (negativePriceAlerts.length ? buildNegativeAlertVoiceText(negativePriceAlerts) : ''),
    [negativePriceAlerts],
  );
  const manualJudgementHint = buildManualJudgementHint(now);
  const declaredPlanSummary = buildDeclaredPlanSummary(declaredPlans, marketDayBounds, now);

  useEffect(() => {
    if (!negativePriceAlertKey) {
      lastSpokenAlertKeyRef.current = '';
      return;
    }
    if (!voiceEnabled || !negativePriceVoiceText) {
      return;
    }
    if (lastSpokenAlertKeyRef.current === negativePriceAlertKey) {
      return;
    }
    if (speakText(negativePriceVoiceText)) {
      lastSpokenAlertKeyRef.current = negativePriceAlertKey;
    }
  }, [negativePriceAlertKey, negativePriceVoiceText, voiceEnabled]);

  const enableVoiceAlert = () => {
    setVoiceEnabled(true);
    window.localStorage.setItem(VOICE_ENABLED_STORAGE_KEY, 'true');
    if (negativePriceAlertKey && negativePriceVoiceText) {
      if (speakText(negativePriceVoiceText)) {
        lastSpokenAlertKeyRef.current = negativePriceAlertKey;
      }
      return;
    }
    speakText('实时调度语音提醒已启用');
  };

  const priceChartOption: EChartsOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' },
      valueFormatter: (value: unknown) => (typeof value === 'number' ? format2(value) : String(value ?? '-')),
    },
    dataZoom: BASE_DATAZOOM,
    legend: {
      top: 0,
      data: ['申报充放电计划', '实时临时电价'],
    },
    grid: { left: 58, right: 78, top: 44, bottom: 82 },
    xAxis: {
      type: 'time',
      min: marketDayBounds.start,
      max: marketDayBounds.end,
      axisLabel: {
        fontSize: 11,
        color: '#6b7280',
        formatter: (value: number) => dayjs(value).format('HH:mm'),
      },
    },
    yAxis: [
      {
        type: 'value',
        name: '申报功率(MW)',
        position: 'left',
        axisLabel: { fontSize: 11, color: '#6b7280' },
        splitLine: { lineStyle: { color: '#f3f4f6' } },
      },
      {
        type: 'value',
        name: '电价(元/MWh)',
        position: 'right',
        axisLabel: { fontSize: 11, color: '#6b7280' },
        splitLine: { show: false },
      },
    ],
    series: [
      {
        name: '申报充放电计划',
        type: 'bar',
        data: buildDeclaredPlanSeries(declaredPlans),
        yAxisIndex: 0,
        barWidth: 8,
        itemStyle: {
          color: (params: { value?: unknown }) => {
            const value = Array.isArray(params.value) ? Number(params.value[1]) : 0;
            return value < 0 ? 'rgba(37, 99, 235, 0.42)' : 'rgba(239, 68, 68, 0.38)';
          },
        },
      },
      {
        name: '实时临时电价',
        type: 'line',
        data: buildPriceSeries(prices),
        yAxisIndex: 1,
        smooth: true,
        lineStyle: { color: '#0d9488', width: 2 },
        itemStyle: { color: '#0d9488' },
        showSymbol: false,
        connectNulls: true,
        markArea: priceRiskAreas.length
          ? {
              silent: true,
              label: { show: false },
              emphasis: { disabled: true },
              data: priceRiskAreas,
            }
          : undefined,
      },
    ],
  };

  return (
    <PageContainer title="实时调度监盘" noCard>
      {negativePriceAlerts.length > 0 && (
        <div style={{ position: 'sticky', top: 0, zIndex: 4, marginBottom: 16 }}>
          <Alert
            type="error"
            showIcon
            message="页面告警：未来 1 小时以上出现负价"
            description={(
              <Space direction="vertical" size={4}>
                <Text strong>
                  {negativePriceAlerts
                    .slice(0, 4)
                    .map((alert) => `${alert.label} ${formatAlertPrice(alert.price)}`)
                    .join('、')}
                </Text>
                <Text>
                  请运维结合 SOC/可充状态，考虑下调超短期功率预测或优先充电吸收。完成调整后在群里回传截图。
                </Text>
              </Space>
            )}
            style={{
              border: '2px solid #dc2626',
              background: '#fef2f2',
              boxShadow: '0 10px 22px rgba(220, 38, 38, 0.14)',
            }}
          />
        </div>
      )}

      {manualJudgementHint && (
        <Alert
          type="warning"
          showIcon
          message={manualJudgementHint.message}
          description={manualJudgementHint.description}
          style={{
            marginBottom: 12,
            border: '1px solid #d97706',
            background: '#fffbeb',
          }}
        />
      )}

      <div
        style={{
          marginBottom: 12,
          padding: '10px 14px',
          border: '1px solid #bfdbfe',
          background: '#eff6ff',
          borderRadius: 6,
        }}
      >
        <Space wrap size={[24, 6]}>
          <Text strong>今日申报充电量：{formatEnergy(declaredPlanSummary.chargeEnergyMwh)}</Text>
          <Text strong>今日申报放电量：{formatEnergy(declaredPlanSummary.dischargeEnergyMwh)}</Text>
          <Text type="secondary">
            未来充电计划：
            {declaredPlanSummary.nextChargeLabel && declaredPlanSummary.nextChargePowerMw !== null
              ? `${declaredPlanSummary.nextChargeLabel} ${formatPower(declaredPlanSummary.nextChargePowerMw)}`
              : '无'}
          </Text>
        </Space>
      </div>

      <ChartCard
        title="实时临时电价监控"
        extra={(
          <Space wrap>
            <Text type="secondary">页面更新：{displayRefreshTime(priceUpdatedAt)}</Text>
            <Button
              size="small"
              danger={negativePriceAlerts.length > 0}
              type={voiceEnabled ? 'primary' : 'default'}
              icon={<SoundOutlined />}
              onClick={enableVoiceAlert}
            >
              {voiceEnabled ? '语音已启用' : '启用语音'}
            </Button>
            <Button size="small" icon={<ReloadOutlined />} onClick={() => refetchPrice()}>
              刷新
            </Button>
          </Space>
        )}
        option={priceChartOption}
        loading={priceLoading}
        error={priceError instanceof Error ? priceError : null}
        onRetry={() => refetchPrice()}
        height={420}
      />
    </PageContainer>
  );
}
